// Platform bridge (ARCHITECTURE §8, docs/NATIVE.md). The only module that touches the shells.
// Mobile: Capacitor 8 plugins found at runtime on window.Capacitor.Plugins (no npm import, the shell provides them).
// Desktop: window.xmDesktop from the Electron preload. Every call is a safe no-op in a plain browser.
import type { ClientKind, LocalNote } from '@shared/types';

// ------------------------------------------------------------------ shell types (subset we use)
type PermState = 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied';
interface Listener { remove(): Promise<void> | void }
type AddListener = (event: string, cb: (arg: any) => void) => Promise<Listener> | Listener;

interface CapPlugins {
  PushNotifications?: {
    requestPermissions(): Promise<{ receive: PermState }>;
    register(): Promise<void>;
    addListener: AddListener;
  };
  LocalNotifications?: {
    requestPermissions(): Promise<{ display: PermState }>;
    schedule(o: { notifications: { id: number; title: string; body: string; schedule?: { at: Date; allowWhileIdle?: boolean }; extra?: unknown; actionTypeId?: string }[] }): Promise<unknown>;
    registerActionTypes?(o: { types: { id: string; actions: { id: string; title: string; foreground?: boolean }[] }[] }): Promise<void>;
    getPending(): Promise<{ notifications: { id: number }[] }>;
    cancel(o: { notifications: { id: number }[] }): Promise<void>;
    addListener: AddListener;
  };
  App?: { addListener: AddListener; exitApp(): Promise<void>; getInfo?(): Promise<{ version: string; build: string }> };
  Haptics?: {
    impact(o: { style: 'HEAVY' | 'MEDIUM' | 'LIGHT' }): Promise<void>;
    notification(o: { type: 'SUCCESS' | 'WARNING' | 'ERROR' }): Promise<void>;
    selectionChanged?(): Promise<void>;
  };
  StatusBar?: { setStyle(o: { style: 'DARK' | 'LIGHT' | 'DEFAULT' }): Promise<void>; setBackgroundColor?(o: { color: string }): Promise<void> };
  SplashScreen?: { hide(o?: { fadeOutDuration?: number }): Promise<void> };
  Share?: { share(o: { title?: string; text?: string; url?: string; files?: string[]; dialogTitle?: string }): Promise<unknown> };
  Filesystem?: { writeFile(o: { path: string; data: string; directory: string; recursive?: boolean }): Promise<{ uri: string }> };
  KeepAwake?: { keepAwake(): Promise<void>; allowSleep(): Promise<void> };
}
interface CapacitorGlobal { isNativePlatform?(): boolean; getPlatform?(): string; Plugins?: CapPlugins }

/** "While you work" switches (0.2.0 shells keep them; D_Desk PREFS without 'wander', which is phase 2). */
export interface DeskPrefs { stay: boolean; notice: boolean; meetings: boolean }
export type PipSize = 's' | 'm' | 'l';
/** Why the small window is not on screen: signed out, switched off, Xiaoman is in front, tucked away with "–", a call
 *  app is in front, or it is still loading. */
export type PipWhy = 'signed-out' | 'off' | 'room' | 'tucked' | 'call' | 'loading';
export interface PipState { shown: boolean; why: PipWhy | null; size: PipSize; quiet: boolean; prefs: DeskPrefs }

export type DesktopEvent =
  | { type: 'notification-click'; id: string; action?: string }
  | { type: 'hotkey-talk' }
  | { type: 'tray'; action: 'open' | 'start' | 'done' | 'talk' | 'quiet1h' | 'quietTomorrow' }
  | { type: 'navigate'; route: string }
  | { type: 'mini-closed' }
  // 0.2.0 shells
  | { type: 'prefs'; prefs: DeskPrefs }                        // every window
  | ({ type: 'pip' } & PipState)                              // the small window only
  | { type: 'activity'; idle: number }                        // the small window, every second while it is shown
  | { type: 'remind'; id: string; cardId: string };           // the small window: a card's time came

export interface TrayState {
  ch: string; title: string; today: string; next: { time: string; title: string } | null; quietUntil: string | null;
  /** 0.2.0: the card being worked on (the Mac menu bar shows its timer, the menu Done). Older shells ignore it. */
  work?: { title: string; startedAt: string } | null;
}
export interface DesktopNote { id: string; title: string; body: string; actions?: { id: string; label: string }[]; silent?: boolean }

/** docs/NATIVE.md. The optional calls exist from the 0.2.0 shells on: feature-detect them, never assume. */
export interface XmDesktop {
  platform: 'mac' | 'windows' | 'linux';
  version: string;
  isMini: boolean;
  notify(n: DesktopNote): void;
  setTray(s: TrayState | null): void;
  openMini(): void;
  closeMini(): void;
  tuck(on: boolean): void;
  focusMain(route?: string): void;
  setLoginItem(on: boolean): Promise<void>;
  getLoginItem(): Promise<boolean>;
  quit(): void;
  onEvent(cb: (e: DesktopEvent) => void): () => void;
  getPrefs?(): Promise<DeskPrefs>;
  setPrefs?(patch: Partial<DeskPrefs>): Promise<DeskPrefs>;
  getPipState?(): Promise<PipState>;
  hidePip?(): void;
  setPipSize?(size: PipSize): void;
  pipDrag?(phase: 'start' | 'move' | 'end'): void;
}

declare global {
  interface Window { Capacitor?: CapacitorGlobal; xmDesktop?: XmDesktop }
}

const w = (typeof window !== 'undefined' ? window : undefined) as (Window & typeof globalThis) | undefined;
const cap = (): CapacitorGlobal | undefined => (w?.Capacitor && w.Capacitor.isNativePlatform?.() ? w.Capacitor : undefined);
const plugins = (): CapPlugins => cap()?.Plugins || {};
const xm = (): XmDesktop | undefined => w?.xmDesktop;
const safe = async <T>(fn: () => Promise<T> | T | undefined): Promise<T | undefined> => {
  try { return await fn(); } catch (e) { console.warn('[native]', e); return undefined; }
};

// ------------------------------------------------------------------ app events (bus)
export type NativeEvent =
  | { type: 'back' }                       // Android hardware back
  | { type: 'resume' } | { type: 'pause' } // app foreground / background
  | { type: 'route'; route: string }       // notification tap, tray, desktop navigate
  | { type: 'talk' }                       // desktop hotkey / tray "Talk to {n}"
  | { type: 'start-next' }                 // tray: Start on the next card
  | { type: 'mini-closed' }
  | { type: 'push-token'; token: string; platform: 'ios' | 'android' }
  | { type: 'desktop-note-action'; id: string; action?: string }
  | { type: 'note-action'; id: string; action: string }          // phone notification button (push or local)
  // A notification was opened: tapped on a phone (push or local, a button too: they all open the app) or clicked on
  // desktop. `id` is the server's note id ('card:<cardId>:0', 'morning:2026-11-03'); `kind` its Notify.kind.
  | { type: 'note-open'; id: string; kind: string }
  // desktop, 0.2.0 shells
  | { type: 'done-now' }                                         // tray / menu bar: Done on the card being worked on
  | { type: 'desk-prefs'; prefs: DeskPrefs }                     // a "While you work" switch changed (any window)
  | { type: 'pip'; state: PipState }                             // small window: on screen or not, and why
  | { type: 'activity'; idle: number }                           // small window: seconds since the last key / mouse input
  | { type: 'remind'; cardId: string };                          // small window: a card's time came while she is on screen

const bus = new Set<(e: NativeEvent) => void>();
export function onNative(cb: (e: NativeEvent) => void): () => void { bus.add(cb); return () => { bus.delete(cb); }; }
const emit = (e: NativeEvent) => bus.forEach((cb) => { try { cb(e); } catch (err) { console.error(err); } });

// ------------------------------------------------------------------ platform
const CLIENTS: ClientKind[] = ['web', 'ios', 'android', 'mac', 'windows', 'desktop-web'];
let clientParam: ClientKind | null = null;
try {
  const q = w ? new URLSearchParams(w.location.search).get('client') : null;
  if (q && (CLIENTS as string[]).includes(q)) { clientParam = q as ClientKind; sessionStorage.setItem('xm.client', q); }
  else { const s = sessionStorage.getItem('xm.client'); if (s && (CLIENTS as string[]).includes(s)) clientParam = s as ClientKind; }
} catch { /* storage blocked */ }

/** The client id sent as X-Client. Shells load the app with ?client=ios|android|mac|windows (NATIVE.md). */
export function platform(): ClientKind {
  if (clientParam) return clientParam;
  const c = cap();
  if (c) { const p = c.getPlatform?.(); if (p === 'ios' || p === 'android') return p; }
  const d = xm();
  if (d) return d.platform === 'mac' ? 'mac' : 'windows';
  return w && w.innerWidth >= 1000 ? 'desktop-web' : 'web';
}
/** The native shell's version (and build number on iPhone/Android); null in a plain browser. Cached. */
let shell: Promise<{ version: string; build?: string } | null> | null = null;
/** shellLabel()'s answer once shellInfo() has one (undefined until then). */
let shellText: string | null | undefined;
const uaShell = () => (w ? /XiaomanApp\/[\w-]+\/([\w.]+)/.exec(w.navigator.userAgent)?.[1] || null : null);
export function shellInfo(): Promise<{ version: string; build?: string } | null> {
  if (!shell) {
    shell = (async () => {
      const d = xm();
      if (d) return { version: d.version };
      const info = await safe(() => plugins().App?.getInfo?.());
      if (info) return { version: info.version, build: info.build };
      const v = uaShell();
      return v ? { version: v } : null;
    })();
    void shell.then((s) => { shellText = s ? s.version + (s.build ? ` (${s.build})` : '') : null; }, () => { shellText = null; });
  }
  return shell;
}
/** The shell as β reports show it, for the X-Shell header: '0.2.0 (14)' (iPhone/Android: version and build number),
 *  '0.2.0' (desktop), null in a plain browser. Synchronous: until shellInfo() has answered (the first request at
 *  boot), the version from xmDesktop or the user agent. */
export function shellLabel(): string | null {
  if (shellText !== undefined) return shellText;
  void shellInfo();
  const d = xm();
  return d?.version ? String(d.version) : uaShell();
}
export const isNativeMobile = () => { const p = platform(); return p === 'ios' || p === 'android'; };
export const isDesktopShell = () => { const p = platform(); return p === 'mac' || p === 'windows'; };
export const isMiniWindow = () => !!xm()?.isMini || (!!w && /^#\/mini\b/.test(w.location.hash));

// ------------------------------------------------------------------ push + local notifications
export type PushMode = 'push' | 'local' | 'none';
let pushMode: PushMode = 'none';
export const currentPushMode = () => pushMode;

/**
 * Ask for notification permission and register for push. Emits { type: 'push-token' } with the token (the store posts
 * it to POST /api/devices). Falls back to local notifications when push is unavailable (no Firebase, denied
 * registration, timeout). Desktop and browsers return 'none' (desktop notifies through xmDesktop.notify).
 */
export async function registerPush(): Promise<PushMode> {
  const P = plugins();
  const plat = platform();
  if (plat !== 'ios' && plat !== 'android') return (pushMode = 'none');
  void registerActions();
  if (P.PushNotifications) {
    const ok = await safe(async () => {
      const perm = await P.PushNotifications!.requestPermissions();
      if (perm.receive !== 'granted') return false;
      return await new Promise<boolean>((resolve) => {
        let done = false;
        const finish = (v: boolean) => { if (!done) { done = true; resolve(v); } };
        void P.PushNotifications!.addListener('registration', (t: { value: string }) => {
          emit({ type: 'push-token', token: t.value, platform: plat });
          finish(true);
        });
        void P.PushNotifications!.addListener('registrationError', () => finish(false));
        setTimeout(() => finish(false), 12000);
        P.PushNotifications!.register().catch(() => finish(false));
      });
    });
    if (ok) return (pushMode = 'push');
  }
  if (P.LocalNotifications) {
    const perm = await safe(() => P.LocalNotifications!.requestPermissions());
    if (perm && perm.display === 'granted') return (pushMode = 'local');
  }
  return (pushMode = 'none');
}

/** 32-bit positive int id for a string id (Capacitor local notification ids are ints). */
export function noteIntId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

/** Notification kind from its id (the server's job key: 'morning:2026-11-02', 'card:<cardId>:0', 'nudge:...'). */
export function noteKind(id: string): string | null {
  const k = String(id).split(':')[0];
  return k === 'morning2' ? 'morning' : ['morning', 'nudge', 'card', 'checkin', 'evening', 'week', 'recall'].includes(k) ? k : null;
}

/**
 * Notification buttons on phones. iOS shows a push's buttons from the category in `aps.category` (= Notify.kind), and
 * categories registered here apply to remote and local notifications alike. Ids and labels mirror the server's
 * Notify.actions (server/src/core/engine.ts noteFor). Foreground: the app opens and handles the action.
 */
const ACTION_TYPES = [
  { id: 'morning', actions: [{ id: 'plan', title: 'Plan now', foreground: true }, { id: 'snooze', title: 'In 30 minutes', foreground: true }] },
  { id: 'nudge', actions: [{ id: 'accept', title: 'Accept', foreground: true }, { id: 'not-today', title: 'Not today', foreground: true }] },
  { id: 'card', actions: [{ id: 'start', title: 'Start', foreground: true }] },
  { id: 'checkin', actions: [{ id: 'start', title: 'Start', foreground: true }] },
];
let actionsRegistered = false;
async function registerActions(): Promise<void> {
  const L = plugins().LocalNotifications;
  if (actionsRegistered || !L?.registerActionTypes) return;
  actionsRegistered = true;
  await safe(() => L.registerActionTypes!({ types: ACTION_TYPES }));
}

/** Replace all pending local notifications with `items` (from GET /api/schedule). Mobile only. */
export async function scheduleLocal(items: LocalNote[]): Promise<void> {
  const L = plugins().LocalNotifications;
  if (!L) return;
  await safe(async () => {
    const pending = await L.getPending();
    if (pending.notifications.length) await L.cancel({ notifications: pending.notifications.map((n) => ({ id: n.id })) });
    const now = Date.now();
    const notes = items.filter((x) => Date.parse(x.at) > now).map((x) => ({
      id: noteIntId(x.id), title: x.title, body: x.body,
      schedule: { at: new Date(x.at), allowWhileIdle: true }, extra: Object.assign({ id: x.id }, x.data || {}),
      actionTypeId: noteKind(x.id) || undefined,
    }));
    if (notes.length) await L.schedule({ notifications: notes });
  });
}

// ------------------------------------------------------------------ keep awake, haptics
let wakeLock: { release(): Promise<void> } | null = null;
/** Keep the screen on while working together (W, O9, K2). */
export async function keepAwake(on: boolean): Promise<void> {
  const K = plugins().KeepAwake;
  if (K) { await safe(() => (on ? K.keepAwake() : K.allowSleep())); return; }
  const nav = w?.navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<{ release(): Promise<void> }> } };
  // Browsers may refuse (hidden page, battery saver, no permission): that's expected, so no warning.
  if (on && nav?.wakeLock && !wakeLock) wakeLock = await nav.wakeLock.request('screen').catch(() => null);
  if (!on && wakeLock) { const l = wakeLock; wakeLock = null; await l.release().catch(() => undefined); }
}

export type HapticKind = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' | 'selection';
export function haptic(kind: HapticKind = 'light'): void {
  const H = plugins().Haptics;
  if (H) {
    if (kind === 'success' || kind === 'warning' || kind === 'error') void safe(() => H.notification({ type: kind.toUpperCase() as 'SUCCESS' }));
    else if (kind === 'selection') void safe(() => (H.selectionChanged ? H.selectionChanged() : H.impact({ style: 'LIGHT' })));
    else void safe(() => H.impact({ style: kind.toUpperCase() as 'LIGHT' }));
    return;
  }
  const nav = w?.navigator;
  if (nav && 'vibrate' in nav && platform() === 'web') { try { nav.vibrate(kind === 'heavy' || kind === 'error' ? 30 : 12); } catch { /* ignore */ } }
}

// ------------------------------------------------------------------ share
export interface ShareInput { title?: string; text?: string; url?: string; file?: { blob: Blob; name: string } }
export type ShareResult = 'shared' | 'saved' | 'cancelled' | 'unavailable';

const blobToB64 = (b: Blob) => new Promise<string>((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(',')[1] || '');
  r.onerror = () => rej(r.error);
  r.readAsDataURL(b);
});

/** Share text or a file (e.g. the SH share card PNG). Mobile: share sheet. Browser: Web Share or download. */
export async function share(x: ShareInput): Promise<ShareResult> {
  const P = plugins();
  if (P.Share) {
    try {
      let files: string[] | undefined;
      if (x.file && P.Filesystem) {
        const f = await P.Filesystem.writeFile({ path: x.file.name, data: await blobToB64(x.file.blob), directory: 'CACHE', recursive: true });
        files = [f.uri];
      }
      await P.Share.share({ title: x.title, text: x.text, url: x.url, files, dialogTitle: x.title });
      return 'shared';
    } catch (e) {
      return /cancel/i.test(String((e as Error)?.message || e)) ? 'cancelled' : 'unavailable';
    }
  }
  const nav = w?.navigator as Navigator | undefined;
  if (nav?.share && !isDesktopShell()) {
    const data: ShareData = { title: x.title, text: x.text, url: x.url };
    if (x.file) {
      const file = new File([x.file.blob], x.file.name, { type: x.file.blob.type });
      if (nav.canShare?.({ files: [file] })) data.files = [file];
    }
    try { await nav.share(data); return 'shared'; } catch (e) { if ((e as Error)?.name === 'AbortError') return 'cancelled'; }
  }
  if (x.file && w) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(x.file.blob);
    a.download = x.file.name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    return 'saved';
  }
  if (x.text && nav?.clipboard) { try { await nav.clipboard.writeText([x.text, x.url].filter(Boolean).join(' ')); return 'saved'; } catch { /* ignore */ } }
  return 'unavailable';
}

// ------------------------------------------------------------------ desktop (Electron)
const noteRoutes = new Map<string, string>();
/** Notify.kind per note id shown on this desktop (for 'note-open'; an id from before a restart falls back to noteKind). */
const noteKinds = new Map<string, string>();
export const desktop = {
  available: () => !!xm(),
  isMini: () => !!xm()?.isMini,
  isMac: () => xm()?.platform === 'mac',
  version: () => xm()?.version || null,
  /** Show a system notification; `route` (e.g. 'C', 'W:<cardId>') opens on click. `kind`: its Notify.kind. */
  notify(n: DesktopNote & { route?: string; kind?: string }) {
    const d = xm();
    if (!d) return;
    if (n.route) noteRoutes.set(n.id, n.route);
    if (n.kind) noteKinds.set(n.id, n.kind);
    try { d.notify({ id: n.id, title: n.title, body: n.body, actions: n.actions, silent: n.silent }); } catch (e) { console.warn(e); }
  },
  /** null = signed out (0.2.0 shells drop her face and the small window; older shells ignore it). */
  setTray(s: TrayState | null) { try { xm()?.setTray(s as TrayState); } catch (e) { console.warn(e); } },
  openMini() { try { xm()?.openMini(); } catch (e) { console.warn(e); } },
  closeMini() { try { xm()?.closeMini(); } catch (e) { console.warn(e); } },
  tuck(on: boolean) { try { xm()?.tuck(on); } catch (e) { console.warn(e); } },
  focusMain(route?: string) { try { xm()?.focusMain(route); } catch (e) { console.warn(e); } },
  async setLoginItem(on: boolean) { await safe(() => xm()?.setLoginItem(on)); },
  async getLoginItem(): Promise<boolean> { return !!(await safe(() => xm()?.getLoginItem())); },
  quit() { try { xm()?.quit(); } catch (e) { console.warn(e); } },
  // ---- 0.2.0 shells (the small window that stays on top + "While you work"). Older shells: null / fallbacks.
  /** The shell has the 0.2.0 small window (PiP). False in a 0.1.0 shell (360x560 cowork window) and in a browser. */
  hasPip: () => typeof xm()?.getPipState === 'function',
  /** The switches, or null when the shell has none (browser, 0.1.0 shell): then don't show them. */
  async getPrefs(): Promise<DeskPrefs | null> { const d = xm(); return d?.getPrefs ? (await safe(() => d.getPrefs!())) || null : null; },
  async setPrefs(patch: Partial<DeskPrefs>): Promise<DeskPrefs | null> { const d = xm(); return d?.setPrefs ? (await safe(() => d.setPrefs!(patch))) || null : null; },
  async pipState(): Promise<PipState | null> { const d = xm(); return d?.getPipState ? (await safe(() => d.getPipState!())) || null : null; },
  /** "–": into the menu bar / tray. A 0.1.0 shell has no such place: its old tuck-to-the-edge tab instead. */
  hidePip() { try { const d = xm(); if (d?.hidePip) d.hidePip(); else d?.tuck(true); } catch (e) { console.warn(e); } },
  setPipSize(size: PipSize) { try { xm()?.setPipSize?.(size); } catch (e) { console.warn(e); } },
  pipDrag(phase: 'start' | 'move' | 'end') { try { xm()?.pipDrag?.(phase); } catch (e) { console.warn(e); } },
};

// ------------------------------------------------------------------ boot
let started = false;
/** Wire shell listeners once at boot. Safe to call in a browser. */
export function initNative(): void {
  if (started || !w) return;
  started = true;
  const P = plugins();
  if (P.App) {
    void safe(() => P.App!.addListener('backButton', () => emit({ type: 'back' })));
    void safe(() => P.App!.addListener('appStateChange', (s: { isActive: boolean }) => emit({ type: s.isActive ? 'resume' : 'pause' })));
  }
  const routeOf = (data: unknown) => (data && typeof data === 'object' && typeof (data as { route?: unknown }).route === 'string' ? (data as { route: string }).route : null);
  // Phone notification taps and buttons, like desktop notification clicks: buttons that change something
  // (snooze / not-today / accept) go to the store (POST /api/notify/action); then route ('start' opens W).
  // Push data (server/src/push/fcm.ts): { noteId, kind, payload: JSON of note.data, actions }. Local: extra = { id, ...data }.
  const act = (id: string | null, kind: string | null, actionId: string | undefined, data: Record<string, unknown>) => {
    // Every tap and button opens the app here (the buttons are foreground actions); a swipe-away never reaches us.
    if (id && actionId !== 'dismiss') emit({ type: 'note-open', id, kind: kind || noteKind(id) || 'unknown' });
    const action = actionId && actionId !== 'tap' ? actionId : null;
    if (id && (action === 'snooze' || action === 'not-today' || action === 'accept')) emit({ type: 'note-action', id, action });
    if (action === 'snooze' || action === 'not-today') return;
    const cardId = typeof data.cardId === 'string' ? data.cardId : null;
    const r = routeOf(data) || (action === 'start' && cardId ? `W:${cardId}` : kind === 'card' || kind === 'checkin' ? 'T' : kind === 'week' ? 'U3' : 'C');
    emit({ type: 'route', route: r });
  };
  if (P.PushNotifications) {
    void safe(() => P.PushNotifications!.addListener('pushNotificationActionPerformed', (a: { actionId?: string; notification?: { data?: Record<string, unknown> } }) => {
      const d = a?.notification?.data || {};
      let payload: Record<string, unknown> = {};
      try { payload = typeof d.payload === 'string' ? JSON.parse(d.payload) || {} : {}; } catch { /* ignore */ }
      act(typeof d.noteId === 'string' ? d.noteId : null, typeof d.kind === 'string' ? d.kind : null, a?.actionId, { ...payload, route: d.route ?? payload.route });
    }));
  }
  if (P.LocalNotifications) {
    void safe(() => P.LocalNotifications!.addListener('localNotificationActionPerformed', (a: { actionId?: string; notification?: { extra?: Record<string, unknown> } }) => {
      const x = a?.notification?.extra || {};
      const id = typeof x.id === 'string' ? x.id : null;
      act(id, id ? noteKind(id) : null, a?.actionId, x);
    }));
  }
  if (P.StatusBar) {
    void safe(() => P.StatusBar!.setStyle({ style: 'DARK' }));
    if (platform() === 'android') void safe(() => P.StatusBar!.setBackgroundColor?.({ color: '#100E0C' }));
  }
  const d = xm();
  if (d) {
    try {
      d.onEvent((e) => {
        if (e.type === 'notification-click') {
          const r = noteRoutes.get(e.id);
          // A click on the notification itself brings the main window up; its buttons don't (they act in place).
          if (e.id && !e.action) emit({ type: 'note-open', id: e.id, kind: noteKinds.get(e.id) || noteKind(e.id) || 'unknown' });
          emit({ type: 'desktop-note-action', id: e.id, action: e.action });
          if (r) emit({ type: 'route', route: r });
        } else if (e.type === 'hotkey-talk') emit({ type: 'talk' });
        else if (e.type === 'navigate') emit({ type: 'route', route: e.route });
        else if (e.type === 'mini-closed') emit({ type: 'mini-closed' });
        else if (e.type === 'tray') {
          if (e.action === 'open') emit({ type: 'route', route: 'C' });
          else if (e.action === 'start') emit({ type: 'start-next' });
          else if (e.action === 'done') emit({ type: 'done-now' });
          else if (e.action === 'talk') emit({ type: 'talk' });
          // quiet1h / quietTomorrow are kept by the shell (it suppresses notify until they end)
        }
        else if (e.type === 'prefs' && e.prefs) emit({ type: 'desk-prefs', prefs: e.prefs });
        else if (e.type === 'pip') emit({ type: 'pip', state: { shown: !!e.shown, why: e.why ?? null, size: e.size, quiet: !!e.quiet, prefs: e.prefs } });
        else if (e.type === 'activity' && typeof e.idle === 'number') emit({ type: 'activity', idle: e.idle });
        else if (e.type === 'remind' && typeof e.cardId === 'string') emit({ type: 'remind', cardId: e.cardId });
      });
    } catch (e) { console.warn(e); }
  }
  document.addEventListener('visibilitychange', () => emit({ type: document.visibilityState === 'visible' ? 'resume' : 'pause' }));
}

/** Hide the native splash once the first screen has rendered. */
export function hideSplash() { void safe(() => plugins().SplashScreen?.hide({ fadeOutDuration: 200 })); }
/** Leave the app (Android back on the home screen). */
export function exitApp() { void safe(() => plugins().App?.exitApp()); }
